import { useEffect, useState } from 'react'
import { api, ErroApi, type ClinicaCadastro } from '../lib/api'
import { useSessao } from '../lib/sessao'
import { Topo, Secao, Carregando } from '../componentes/ui'
import './identidade.css'

/**
 * Quem é esta clínica.
 *
 * ─── POR QUE ESTA TELA NÃO EXISTIA ────────────────────────────────
 *
 * Porque o dado não existia. `public.clinica` tinha nove colunas —
 * id, slug, nome, vertical, timezone, status, trial_ate e as duas de
 * carimbo — e nenhuma dizia onde a clínica fica, qual o CNPJ ou como
 * ligar para ela. A 0036 abriu as treze colunas que faltavam.
 *
 * Não é cadastro por cadastro. São quatro coisas que já estavam
 * quebradas por falta dele:
 *
 *  · o recibo sem CNPJ e endereço não serve para reembolso de convênio;
 *  · a ficha assinada carimba o profissional e não diz o estabelecimento;
 *  · `/agendar/<slug>` manda o paciente a um endereço desconhecido;
 *  · duas unidades eram indistinguíveis uma da outra.
 *
 * ─── O QUE ESTA TELA NÃO DEIXA MUDAR ──────────────────────────────
 *
 * `status`, `trial_ate` e `slug` aparecem como LEITURA. Não é preguiça
 * de formulário: a 0036 concedeu o UPDATE coluna a coluna e deixou as
 * três de fora, porque as duas primeiras são a chave da cobrança e a
 * terceira é a URL pública, que já foi entregue a paciente. Mostrar o
 * campo desabilitado seria mentir sobre o que a tela pode; mostrar como
 * informação é honesto.
 */

const UFS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
             'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO']

/* Máscara na EXIBIÇÃO, dígito no banco. Guardar formatado impede
   comparar e obriga a normalizar em toda consulta — mesma decisão do
   CPF do paciente. A API normaliza de volta ao receber. */
const mascaraCnpj = (v: string) => {
  const d = v.replace(/\D+/g, '').slice(0, 14)
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
}
const mascaraCep = (v: string) => {
  const d = v.replace(/\D+/g, '').slice(0, 8)
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d
}
const mascaraTelefone = (v: string) => {
  const d = v.replace(/\D+/g, '').slice(0, 11)
  if (d.length <= 10) {
    return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2')
  }
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2')
}

type Campos = Partial<Record<keyof ClinicaCadastro, string>>

export function Identidade() {
  const { pode, recarregar } = useSessao()
  const podeEditar = pode('config:write')

  const [clinica, setClinica] = useState<ClinicaCadastro | null>(null)
  const [form, setForm] = useState<Campos>({})
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [salvo, setSalvo] = useState(false)

  useEffect(() => { void carregar() }, [])

  async function carregar() {
    setCarregando(true); setErro(null)
    try {
      const c = await api<ClinicaCadastro>('/v1/clinica')
      setClinica(c)
      setForm({
        nome: c.nome ?? '',
        razao_social: c.razao_social ?? '',
        cnpj: c.cnpj ? mascaraCnpj(c.cnpj) : '',
        inscricao_municipal: c.inscricao_municipal ?? '',
        telefone: c.telefone ? mascaraTelefone(c.telefone) : '',
        email: c.email ?? '',
        site: c.site ?? '',
        cep: c.cep ? mascaraCep(c.cep) : '',
        logradouro: c.logradouro ?? '',
        numero: c.numero ?? '',
        complemento: c.complemento ?? '',
        bairro: c.bairro ?? '',
        cidade: c.cidade ?? '',
        uf: c.uf ?? '',
      })
    } catch {
      setErro('Não foi possível carregar os dados da clínica.')
    }
    setCarregando(false)
  }

  const mudar = (campo: keyof ClinicaCadastro, valor: string) => {
    setSalvo(false)
    setForm((f) => ({ ...f, [campo]: valor }))
  }

  async function salvar(e: React.FormEvent) {
    e.preventDefault()
    setSalvando(true); setErro(null); setSalvo(false)
    try {
      const atualizada = await api<ClinicaCadastro>('/v1/clinica', {
        metodo: 'PATCH',
        // Manda tudo: a API tem allowlist própria e o banco tem GRANT
        // por coluna. Mandar só o que mudou exigiria comparar campo a
        // campo aqui e erraria no primeiro campo apagado de propósito.
        corpo: form,
      })
      setClinica(atualizada)
      setSalvo(true)
      /* O nome da clínica aparece no topo e no seletor de unidade —
         sem recarregar o contexto, a pessoa renomeia e continua vendo
         o nome antigo lá em cima. */
      if (atualizada.nome !== clinica?.nome) await recarregar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível salvar.')
    }
    setSalvando(false)
  }

  if (carregando) return <Carregando altura={320} />
  if (!clinica) {
    return (
      <>
        <Topo titulo="Identidade" />
        <div className="aviso aviso--erro" role="alert">
          {erro ?? 'Clínica não encontrada.'}
        </div>
      </>
    )
  }

  /* Veredito por seção, no título — §8 das referências. O critério é o
     do RECIBO, não "todo campo preenchido": razão social e inscrição
     municipal ficam de fora porque nem toda clínica é PJ. */
  const temEndereco = !!(form.logradouro && form.cidade && form.uf)
  const temContato = !!form.telefone
  const temFiscal = !!form.cnpj

  return (
    <form className="idt" onSubmit={salvar}>
      <Topo titulo="Identidade"
            subtitulo="Quem é esta unidade. Vai no recibo, na ficha assinada e na página de agendamento.">
        {podeEditar
          ? (
            <button className="botao" type="submit" disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          )
          : <span className="selo selo--neutro">só leitura</span>}
      </Topo>

      {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}
      {salvo && <div className="aviso aviso--ok" role="status">Dados da clínica atualizados.</div>}

      <div className="idt__colunas">
        <div className="idt__principal">
          <Secao titulo="Nome e documento"
                 descricao="O nome fantasia aparece na tela; a razão social e o CNPJ aparecem no recibo."
                 veredito={temFiscal
                   ? { texto: 'com CNPJ', tom: 'ok' }
                   : { texto: 'sem CNPJ', tom: 'atencao' }}>
            <div className="idt__campos">
              <Campo id="nome" rotulo="Nome fantasia" larg="dois"
                     valor={form.nome ?? ''} editavel={podeEditar}
                     aoMudar={(v) => mudar('nome', v)}
                     dica="É o nome que a equipe e o paciente veem." />
              <Campo id="razao" rotulo="Razão social" larg="dois"
                     valor={form.razao_social ?? ''} editavel={podeEditar}
                     aoMudar={(v) => mudar('razao_social', v)}
                     dica="Deixe em branco se a clínica é você, pessoa física." />
              <Campo id="cnpj" rotulo="CNPJ" valor={form.cnpj ?? ''} editavel={podeEditar}
                     aoMudar={(v) => mudar('cnpj', mascaraCnpj(v))}
                     placeholder="00.000.000/0000-00" inputMode="numeric"
                     dica="Sem ele o paciente não consegue reembolso do convênio." />
              <Campo id="im" rotulo="Inscrição municipal"
                     valor={form.inscricao_municipal ?? ''} editavel={podeEditar}
                     aoMudar={(v) => mudar('inscricao_municipal', v)}
                     dica="Só onde a prefeitura exige na nota." />
            </div>
          </Secao>

          <Secao titulo="Onde a clínica fica"
                 descricao="O paciente vai a este endereço. Ele aparece na página pública de agendamento."
                 veredito={temEndereco
                   ? { texto: 'completo', tom: 'ok' }
                   : { texto: 'incompleto', tom: 'atencao' }}>
            <div className="idt__campos">
              <Campo id="cep" rotulo="CEP" valor={form.cep ?? ''} editavel={podeEditar}
                     aoMudar={(v) => mudar('cep', mascaraCep(v))}
                     placeholder="00000-000" inputMode="numeric" />
              <Campo id="logradouro" rotulo="Rua, avenida" larg="tres"
                     valor={form.logradouro ?? ''} editavel={podeEditar}
                     aoMudar={(v) => mudar('logradouro', v)} />
              <Campo id="numero" rotulo="Número" valor={form.numero ?? ''} editavel={podeEditar}
                     aoMudar={(v) => mudar('numero', v)} />
              <Campo id="complemento" rotulo="Complemento" valor={form.complemento ?? ''}
                     editavel={podeEditar} aoMudar={(v) => mudar('complemento', v)}
                     placeholder="Sala, andar, bloco" />
              <Campo id="bairro" rotulo="Bairro" larg="dois" valor={form.bairro ?? ''}
                     editavel={podeEditar} aoMudar={(v) => mudar('bairro', v)} />
              <Campo id="cidade" rotulo="Cidade" larg="tres" valor={form.cidade ?? ''}
                     editavel={podeEditar} aoMudar={(v) => mudar('cidade', v)} />
              <div className="campo">
                <label htmlFor="uf">UF</label>
                <select id="uf" className="entrada" value={form.uf ?? ''}
                        disabled={!podeEditar}
                        onChange={(e) => mudar('uf', e.target.value)}>
                  <option value="">—</option>
                  {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
          </Secao>

          <Secao titulo="Como falam com a clínica"
                 descricao="Telefone e e-mail do estabelecimento, não os seus."
                 veredito={temContato
                   ? { texto: 'com telefone', tom: 'ok' }
                   : { texto: 'sem telefone', tom: 'atencao' }}>
            <div className="idt__campos">
              <Campo id="telefone" rotulo="Telefone" larg="dois"
                     valor={form.telefone ?? ''} editavel={podeEditar}
                     aoMudar={(v) => mudar('telefone', mascaraTelefone(v))}
                     placeholder="(00) 00000-0000" inputMode="tel" />
              <Campo id="email" rotulo="E-mail" larg="dois" tipo="email"
                     valor={form.email ?? ''} editavel={podeEditar}
                     aoMudar={(v) => mudar('email', v)}
                     placeholder="contato@suaclinica.com.br" />
              <Campo id="site" rotulo="Site" larg="tres" valor={form.site ?? ''}
                     editavel={podeEditar} aoMudar={(v) => mudar('site', v)}
                     placeholder="https://" />
            </div>
          </Secao>
        </div>

        {/* A coluna estreita é onde se CONSULTA, não onde se age — §2
            das referências. Aqui mora o que a clínica não pode mudar. */}
        <aside className="idt__lado">
          <Secao titulo="Fixo desta unidade"
                 descricao="Só o suporte muda estes.">
            <dl className="idt__fixo">
              <div>
                <dt>Endereço público</dt>
                <dd className="mono">clinuma.com.br/agendar/{clinica.slug}</dd>
                <p className="ajuda">
                  Trocar quebraria os links já entregues a paciente.
                </p>
              </div>
              <div>
                <dt>Situação da conta</dt>
                <dd>
                  <span className={`selo selo--${clinica.status === 'ativo' ? 'ok'
                    : clinica.status === 'trial' ? 'neutro' : 'atencao'}`}>
                    {clinica.status}
                  </span>
                  {clinica.trial_ate && clinica.status === 'trial' && (
                    <span className="ajuda"> até {new Date(clinica.trial_ate).toLocaleDateString('pt-BR')}</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>Especialidade base</dt>
                <dd>{clinica.vertical}</dd>
                <p className="ajuda">Define quais modelos de prontuário se aplicam.</p>
              </div>
              <div>
                <dt>Fuso</dt>
                <dd className="mono">{clinica.timezone}</dd>
                <p className="ajuda">Toda hora da agenda é lida neste fuso.</p>
              </div>
            </dl>
          </Secao>
        </aside>
      </div>
    </form>
  )
}

/**
 * Campo de formulário.
 *
 * `larg` decide quantas colunas da grade ele ocupa. A grade tem altura
 * de fileira constante e larguras variáveis — §2 das referências: é o
 * que permite ler a coluna de cima a baixo sem o olho tropeçar.
 */
function Campo({ id, rotulo, valor, aoMudar, editavel, dica, placeholder, larg, tipo, inputMode }: {
  id: string
  rotulo: string
  valor: string
  aoMudar: (v: string) => void
  editavel: boolean
  dica?: string
  placeholder?: string
  larg?: 'dois' | 'tres'
  tipo?: string
  inputMode?: 'numeric' | 'tel' | 'email'
}) {
  return (
    <div className={`campo${larg ? ` idt__campo--${larg}` : ''}`}>
      <label htmlFor={id}>{rotulo}</label>
      <input id={id} className="entrada" type={tipo ?? 'text'}
             value={valor} disabled={!editavel}
             placeholder={placeholder}
             {...(inputMode ? { inputMode } : {})}
             onChange={(e) => aoMudar(e.target.value)} />
      {dica && <span className="ajuda">{dica}</span>}
    </div>
  )
}
